import { useNavigate } from 'react-router-dom';
import { TasksInExecution, Topbar, type TopbarMiddleware } from '@zarya/ui';
import type { Shell } from '@app/api/hooks/shell';
import { useLogout } from '@app/api/hooks/shell';
import { useCurrentUser } from '@app/auth/useCurrentUser';
import { tenantLabelText } from '@app/ambiente';
import { dataHour } from '@app/format';
import { ROUTE_LOGIN } from '@app/navigation';
import { t } from '@app/i18n';

export interface ShellTopbarProps {
  /** `getShell.middleware` (FR-013). */
  middleware?: Shell['middleware'] | undefined;
  /** Pendências do sino, de `getApprovalsSummary` (FR-013). */
  pendentes?: number;
  /** Abre a busca global (FR-014; a busca em si vem na T106). */
  onSearch?: () => void;
}

/**
 * Tooltip do chip: URL, última sincronização e versão do executor (FR-013).
 * Sai vazio quando o shell ainda não trouxe nenhum dos três.
 */
function middlewareDetail(middleware?: Shell['middleware']): string | null {
  if (!middleware) return null;
  const sync = dataHour(middleware.last_sync_at);
  const parts = [
    middleware.url,
    sync ? t('shell.topbar.lastSync', { when: sync }) : null,
    middleware.version ? t('shell.topbar.version', { version: middleware.version }) : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Barra superior do console (T2): chip do executor, sino de pendências, tenant e
 * menu do usuário com **Sair** (FR-008, FR-013, FR-014, FR-015).
 */
export function ShellTopbar({ middleware, pendentes = 0, onSearch }: ShellTopbarProps) {
  const navigate = useNavigate();
  const { usuario, tenant, encerrar } = useCurrentUser();
  const logout = useLogout();

  const chip: TopbarMiddleware | undefined = middleware
    ? {
        estado: middleware.state,
        versao: middleware.version,
        detalhe: middlewareDetail(middleware),
      }
    : undefined;

  return (
    <Topbar
      middleware={chip}
      tenant={
        tenant?.client
          ? { cliente: tenant.client, ambiente: tenantLabelText(tenant.label) }
          : undefined
      }
      user={
        usuario
          ? {
              nome: usuario.name,
              papel: usuario.role,
              email: usuario.email,
              googleVinculado: usuario.google_linked,
            }
          : undefined
      }
      onSearch={onSearch}
      notificationsCount={pendentes}
      onNotifications={() => navigate('/aprovacoes')}
      logoutPending={logout.isPending}
      onLogout={() => {
        // O cookie é httpOnly: quem o expira é o backend. Aqui só saímos da tela —
        // o cache já foi limpo pelo hook, com ou sem erro (FR-008).
        logout.mutate(undefined, {
          onSettled: () => {
            encerrar();
            navigate(ROUTE_LOGIN, { replace: true });
          },
        });
      }}
    >
      {/* Ações demoradas em voo (29/08): indicador não bloqueante ao lado do chip
          do executor. Sem tarefa, não renderiza nada. */}
      <TasksInExecution />
    </Topbar>
  );
}
